import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import QuillEditor from "../../common/QuillEditor";
import {
  Save,
  ArrowLeft,
  Loader2,
  ImagePlus,
  X,
  Star,
  GripVertical,
} from "lucide-react";

export default function AddProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeLang, setActiveLang] = useState("tr");

  const [formData, setFormData] = useState({
    nameTr: "",
    nameEn: "",
    nameAr: "",
    descriptionTr: "",
    descriptionEn: "",
    descriptionAr: "",
    shortDescriptionTr: "",
    shortDescriptionEn: "",
    shortDescriptionAr: "",
    price: "",
    discountPrice: "",
    costPrice: "",
    stock: 0,
    minStock: 0,
    trackQuantity: true,
    allowBackorder: false,
    sku: "",
    barcode: "",
    weight: "",
    dimensions: "",
    slug: "",
    seoTitle: "",
    metaDescription: "",
    metaKeywords: "",
    isActive: true,
    isFeatured: false,
    isDigital: false,
    requiresShipping: true,
    sortOrder: 0,
    productCategoryId: "",
    brandId: "",
  });

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, catRes, productRes] = await Promise.all([
          api.get("/setting"),
          api.get("/productcategory").catch(() => ({ data: [] })),
          id ? api.get(`/product/${id}`) : Promise.resolve(null),
        ]);
        setSettings(settingsRes.data);
        setCategories(catRes.data || []);

        if (settingsRes.data) {
          if (settingsRes.data.turkish) setActiveLang("tr");
          else if (settingsRes.data.english) setActiveLang("en");
          else if (settingsRes.data.arabic) setActiveLang("ar");
        }

        if (productRes) {
          const d = productRes.data;
          setFormData({
            nameTr: d.nameTr || "",
            nameEn: d.nameEn || "",
            nameAr: d.nameAr || "",
            descriptionTr: d.descriptionTr || "",
            descriptionEn: d.descriptionEn || "",
            descriptionAr: d.descriptionAr || "",
            shortDescriptionTr: d.shortDescriptionTr || "",
            shortDescriptionEn: d.shortDescriptionEn || "",
            shortDescriptionAr: d.shortDescriptionAr || "",
            price: d.price ?? "",
            discountPrice: d.discountPrice ?? "",
            costPrice: d.costPrice ?? "",
            stock: d.stock ?? 0,
            minStock: d.minStock ?? 0,
            trackQuantity: d.trackQuantity ?? true,
            allowBackorder: d.allowBackorder ?? false,
            sku: d.sku || "",
            barcode: d.barcode || "",
            weight: d.weight ?? "",
            dimensions: d.dimensions || "",
            slug: d.slug || "",
            seoTitle: d.seoTitle || "",
            metaDescription: d.metaDescription || "",
            metaKeywords: d.metaKeywords || "",
            isActive: d.isActive ?? true,
            isFeatured: d.isFeatured ?? false,
            isDigital: d.isDigital ?? false,
            requiresShipping: d.requiresShipping ?? true,
            sortOrder: d.sortOrder ?? 0,
            productCategoryId: d.productCategoryId ?? "",
            brandId: d.brandId ?? "",
          });
          setExistingImages(
            (d.productImages || [])
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((img) => ({
                ...img,
                url: `${process.env.REACT_APP_UPLOAD_URL}/${img.imagePath}`,
              }))
          );
          if (d.slug) setSlugTouched(true);
        }
      } catch {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateSlug = (title) =>
    title
      .toLowerCase()
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "nameTr" && !slugTouched) {
      newData.slug = generateSlug(value);
    }
    setFormData(newData);
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setFormData((p) => ({ ...p, slug: e.target.value }));
  };

  const handleNewImages = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setNewImages((prev) => [...prev, ...files]);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setNewImagePreviews((prev) => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId) => {
    if (!window.confirm("Bu görseli silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/product/${id}/images/${imageId}`);
      setExistingImages((prev) =>
        prev.filter((img) => img.productImageId !== imageId)
      );
      toast.success("Görsel silindi");
    } catch {
      toast.error("Görsel silinemedi");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = new FormData();
    data.append("NameTr", formData.nameTr);
    data.append("NameEn", formData.nameEn);
    data.append("NameAr", formData.nameAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("ShortDescriptionTr", formData.shortDescriptionTr);
    data.append("ShortDescriptionEn", formData.shortDescriptionEn);
    data.append("ShortDescriptionAr", formData.shortDescriptionAr);
    data.append("Price", formData.price || 0);
    data.append("DiscountPrice", formData.discountPrice || 0);
    data.append("CostPrice", formData.costPrice || 0);
    data.append("Stock", formData.stock || 0);
    data.append("MinStock", formData.minStock || 0);
    data.append("TrackQuantity", formData.trackQuantity);
    data.append("AllowBackorder", formData.allowBackorder);
    data.append("SKU", formData.sku);
    data.append("Barcode", formData.barcode);
    data.append("Weight", formData.weight || 0);
    data.append("Dimensions", formData.dimensions);
    data.append("Slug", formData.slug);
    data.append("SeoTitle", formData.seoTitle);
    data.append("MetaDescription", formData.metaDescription);
    data.append("MetaKeywords", formData.metaKeywords);
    data.append("IsActive", formData.isActive);
    data.append("IsFeatured", formData.isFeatured);
    data.append("IsDigital", formData.isDigital);
    data.append("RequiresShipping", formData.requiresShipping);
    data.append("SortOrder", formData.sortOrder || 0);
    if (formData.productCategoryId)
      data.append("ProductCategoryId", formData.productCategoryId);
    if (formData.brandId) data.append("BrandId", formData.brandId);

    const imageKey = id ? "newImages" : "images";
    newImages.forEach((file) => {
      data.append(imageKey, file);
    });

    try {
      if (id) {
        await api.put(`/product/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/product", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Ürün eklendi");
      }
      navigate("/product-list");
    } catch {
      toast.error("Ürün kaydedilirken hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const langs = [];
  if (settings?.turkish) langs.push({ key: "tr", label: "TR", flag: "🇹🇷" });
  if (settings?.english) langs.push({ key: "en", label: "EN", flag: "🇬🇧" });
  if (settings?.arabic) langs.push({ key: "ar", label: "AR", flag: "🇸🇦" });

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );
  if (!settings)
    return (
      <div className="page-loading">
        <p>Ayarlar bulunamadı.</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Ürün Düzenle" : "Yeni Ürün Ekle"}</h2>
          <p className="page-subtitle">
            {id ? "Mevcut ürünü düzenleyin" : "Yeni bir ürün oluşturun"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/product-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          <div className="form-main">
            {/* Dil Tabları */}
            {langs.length > 1 && (
              <div className="lang-tabs">
                {langs.map((lang) => (
                  <button
                    key={lang.key}
                    type="button"
                    className={`lang-tab ${activeLang === lang.key ? "active" : ""}`}
                    onClick={() => setActiveLang(lang.key)}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>
            )}

            {/* Türkçe */}
            <div
              className="lang-tab-content"
              style={{ display: activeLang === "tr" ? "block" : "none" }}
            >
              {settings.turkish && (
                <div className="content-card">
                  <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                  <div className="form-field">
                    <label>Ürün Adı</label>
                    <input
                      type="text"
                      name="nameTr"
                      value={formData.nameTr}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Türkçe ürün adı"
                    />
                  </div>
                  <div className="form-field">
                    <label>Kısa Açıklama</label>
                    <textarea
                      name="shortDescriptionTr"
                      value={formData.shortDescriptionTr}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="Kısa ürün açıklaması"
                    />
                  </div>
                  <div className="form-field">
                    <label>Detaylı Açıklama</label>
                    <QuillEditor
                      value={formData.descriptionTr}
                      onChange={(c) =>
                        setFormData((p) => ({ ...p, descriptionTr: c }))
                      }
                      placeholder="Türkçe detaylı açıklama"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* English */}
            <div
              className="lang-tab-content"
              style={{ display: activeLang === "en" ? "block" : "none" }}
            >
              {settings.english && (
                <div className="content-card">
                  <h3 className="card-title">🇬🇧 English Content</h3>
                  <div className="form-field">
                    <label>Product Name</label>
                    <input
                      type="text"
                      name="nameEn"
                      value={formData.nameEn}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="English product name"
                    />
                  </div>
                  <div className="form-field">
                    <label>Short Description</label>
                    <textarea
                      name="shortDescriptionEn"
                      value={formData.shortDescriptionEn}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="Short product description"
                    />
                  </div>
                  <div className="form-field">
                    <label>Detailed Description</label>
                    <QuillEditor
                      value={formData.descriptionEn}
                      onChange={(c) =>
                        setFormData((p) => ({ ...p, descriptionEn: c }))
                      }
                      placeholder="English detailed description"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Arabic */}
            <div
              className="lang-tab-content"
              style={{ display: activeLang === "ar" ? "block" : "none" }}
            >
              {settings.arabic && (
                <div className="content-card">
                  <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                  <div className="form-field">
                    <label>اسم المنتج</label>
                    <input
                      type="text"
                      name="nameAr"
                      value={formData.nameAr}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="اسم المنتج"
                    />
                  </div>
                  <div className="form-field">
                    <label>وصف قصير</label>
                    <textarea
                      name="shortDescriptionAr"
                      value={formData.shortDescriptionAr}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="وصف قصير للمنتج"
                      dir="rtl"
                    />
                  </div>
                  <div className="form-field">
                    <label>الوصف التفصيلي</label>
                    <QuillEditor
                      value={formData.descriptionAr}
                      onChange={(c) =>
                        setFormData((p) => ({ ...p, descriptionAr: c }))
                      }
                      placeholder="الوصف التفصيلي"
                      dir="rtl"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Fiyatlandırma */}
            <div className="content-card">
              <h3 className="card-title">Fiyatlandırma</h3>
              <div className="form-grid-3">
                <div className="form-field">
                  <label>Fiyat (₺)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="form-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div className="form-field">
                  <label>İndirimli Fiyat (₺)</label>
                  <input
                    type="number"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleChange}
                    className="form-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div className="form-field">
                  <label>Maliyet Fiyatı (₺)</label>
                  <input
                    type="number"
                    name="costPrice"
                    value={formData.costPrice}
                    onChange={handleChange}
                    className="form-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Stok */}
            <div className="content-card">
              <h3 className="card-title">Stok Yönetimi</h3>
              <div className="form-grid-3">
                <div className="form-field">
                  <label>Stok Adedi</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    className="form-input"
                    min="0"
                  />
                </div>
                <div className="form-field">
                  <label>Minimum Stok</label>
                  <input
                    type="number"
                    name="minStock"
                    value={formData.minStock}
                    onChange={handleChange}
                    className="form-input"
                    min="0"
                  />
                </div>
                <div className="form-field">
                  <label>SKU</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Otomatik oluşturulur"
                  />
                </div>
              </div>
              <div className="form-grid-3" style={{ marginTop: 12 }}>
                <div className="form-field">
                  <label>Barkod</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Barkod numarası"
                  />
                </div>
                <div className="form-field">
                  <label className="toggle-label">
                    <span>Stok Takibi</span>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="trackQuantity"
                        checked={formData.trackQuantity}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </label>
                </div>
                <div className="form-field">
                  <label className="toggle-label">
                    <span>Ön Siparişe İzin Ver</span>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="allowBackorder"
                        checked={formData.allowBackorder}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </label>
                </div>
              </div>
            </div>

            {/* Kargo */}
            <div className="content-card">
              <h3 className="card-title">Kargo & Fiziksel Bilgiler</h3>
              <div className="form-grid-3">
                <div className="form-field">
                  <label>Ağırlık (kg)</label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    className="form-input"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div className="form-field">
                  <label>Boyutlar (GxYxD cm)</label>
                  <input
                    type="text"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="30x20x10"
                  />
                </div>
                <div className="form-field" style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
                  <label className="toggle-label" style={{ flex: 1 }}>
                    <span>Dijital Ürün</span>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isDigital"
                        checked={formData.isDigital}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </label>
                  <label className="toggle-label" style={{ flex: 1 }}>
                    <span>Kargo Gerekli</span>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="requiresShipping"
                        checked={formData.requiresShipping}
                        onChange={handleChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </label>
                </div>
              </div>
            </div>

            {/* Görseller */}
            <div className="content-card">
              <h3 className="card-title">Ürün Görselleri</h3>

              {existingImages.length > 0 && (
                <div className="product-images-grid">
                  {existingImages.map((img) => (
                    <div
                      key={img.productImageId}
                      className="product-image-item"
                    >
                      <img src={img.url} alt="" />
                      {img.isMain && (
                        <span className="product-image-badge">Ana</span>
                      )}
                      <button
                        type="button"
                        className="product-image-remove"
                        onClick={() =>
                          handleDeleteExistingImage(img.productImageId)
                        }
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {newImagePreviews.length > 0 && (
                <div className="product-images-grid" style={{ marginTop: 12 }}>
                  {newImagePreviews.map((preview, index) => (
                    <div key={index} className="product-image-item new">
                      <img src={preview} alt="" />
                      <span className="product-image-badge new-badge">Yeni</span>
                      <button
                        type="button"
                        className="product-image-remove"
                        onClick={() => removeNewImage(index)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <label className="upload-area" style={{ marginTop: 12 }}>
                <ImagePlus size={32} />
                <span>Görsel Yükle (Birden fazla seçebilirsiniz)</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleNewImages}
                  hidden
                />
              </label>
            </div>
          </div>

          {/* Sağ Kolon */}
          <div className="form-sidebar">
            <div className="content-card">
              <h3 className="card-title">Kategori & Marka</h3>
              <div className="form-field">
                <label>Ürün Kategorisi</label>
                <select
                  name="productCategoryId"
                  value={formData.productCategoryId}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map((c) => (
                    <option key={c.productCategoryId} value={c.productCategoryId}>
                      {c.parentCategoryId ? "↳ " : ""}
                      {c.nameTr || c.nameEn}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Durum</h3>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Aktif</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Öne Çıkan</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isFeatured"
                      checked={formData.isFeatured}
                      onChange={handleChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
              <div className="form-field">
                <label>Sıralama</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                />
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">SEO Ayarları</h3>
              <div className="form-field">
                <label>URL (Slug)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  className="form-input"
                  placeholder="urun-slug"
                />
              </div>
              <div className="form-field">
                <label>
                  SEO Başlık{" "}
                  <small className="char-count">
                    {formData.seoTitle.length}/60
                  </small>
                </label>
                <input
                  type="text"
                  name="seoTitle"
                  value={formData.seoTitle}
                  onChange={handleChange}
                  className="form-input"
                  maxLength={60}
                />
              </div>
              <div className="form-field">
                <label>
                  Meta Açıklama{" "}
                  <small className="char-count">
                    {formData.metaDescription.length}/160
                  </small>
                </label>
                <textarea
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  className="form-input"
                  rows={3}
                  maxLength={160}
                />
              </div>
              <div className="form-field">
                <label>Meta Anahtar Kelimeler</label>
                <input
                  type="text"
                  name="metaKeywords"
                  value={formData.metaKeywords}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="anahtar, kelime, seo"
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
